import { useMemo, useRef, useState } from 'react';

import { faSquareArrowUpRight } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { ResizableDraggable } from '@repo/videa-components';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { createPortal } from 'react-dom';

import { PerioChart } from '@/common-components/composed/PerioChart/PerioChart';
import type { PerioChartResponse } from '@/common-components/composed/PerioChart/types';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { perioChartThemeAtom } from '../../atoms';

import { IconPill } from './IconPill';
import { StageIconPill } from './StageIconPill';

interface Props {
  hasCalculus: boolean;
  numberOfQuadrantsWithPerioRecs: number;
  perioChartData: PerioChartResponse;
  stage?: number;
  visitSummary?: TVisitSummary;
}

const DEFAULT_WIDTH_PERCENT = 0.8;
const DEFAULT_HEIGHT_PERCENT = 0.9;
const MAX_WIDTH = 1200;

export const PerioChartDialog = ({
  hasCalculus,
  numberOfQuadrantsWithPerioRecs,
  perioChartData,
  stage,
  visitSummary,
}: Props) => {
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const [perioChartTheme, setPerioChartTheme] = useAtom(perioChartThemeAtom);
  const [isOpen, setIsOpen] = useState(false);
  const isDarkMode = perioChartTheme === 'dark';
  const hasFiredDragEventRef = useRef(false);

  const defaultSizeAndPosition = useMemo(() => {
    const width = Math.min(
      MAX_WIDTH,
      Math.round(window.innerWidth * DEFAULT_WIDTH_PERCENT),
    );
    const height = Math.round(window.innerHeight * DEFAULT_HEIGHT_PERCENT);
    return {
      x: Math.max(0, (window.innerWidth - width) / 2),
      y: Math.max(0, (window.innerHeight - height) / 2),
      width,
      height,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isOpen triggers recenter on each open
  }, [isOpen]);

  const rndContent = (
    <ResizableDraggable
      bounds="window"
      disableCloseOnOverlayClick
      default={defaultSizeAndPosition}
      minHeight={400}
      minWidth={500}
      maxWidth={MAX_WIDTH}
      onClose={() => setIsOpen(false)}
      headerContent={
        <div className="vai-flex vai-flex-col vai-gap-2 vai-min-w-0">
          <div className="vai-flex vai-items-center vai-gap-3">
            <p
              className={classNames('vai-font-inter vai-font-semibold', {
                'vai-text-[#D4D4D8]': isDarkMode,
              })}
            >
              <Trans>Periodontal Data Chart</Trans>
            </p>
            <StageIconPill
              examDate={perioChartData.examDate}
              hasCalculus={hasCalculus}
              isPerioPatient={!!visitSummary?.isPerioPatient}
              mode={perioChartTheme}
              numberOfQuadrantsWithPerioRecs={numberOfQuadrantsWithPerioRecs}
              stage={stage}
              toothCount={perioChartData.teeth.length}
            />
          </div>
          <p
            className={classNames('vai-text-[12px] vai-font-normal', {
              'vai-text-zinc-300': isDarkMode,
              'vai-text-zinc-900': !isDarkMode,
            })}
          >
            <Trans>
              SRP recommendations for the visit are based on this perio chart
            </Trans>
          </p>
        </div>
      }
      enableThemeToggle
      mode={perioChartTheme}
      onThemeToggle={() => {
        const newTheme = isDarkMode ? 'light' : 'dark';
        trackEvent(mixpanelEvents.perioChartThemeToggle, {
          theme: newTheme,
          examDate: perioChartData.examDate,
          toothCount: perioChartData.teeth.length,
          isPerioPatient: !!visitSummary?.isPerioPatient,
          perioStage: stage ?? 'N/A',
          hasCalculusAtBalancedThreshold: hasCalculus,
          numberOfQuadrantsWithPerioRecs,
        });
        setPerioChartTheme(newTheme);
      }}
      onResizeStart={() => {
        trackEvent(mixpanelEvents.perioChartResize, {
          examDate: perioChartData.examDate,
          toothCount: perioChartData.teeth.length,
          isPerioPatient: !!visitSummary?.isPerioPatient,
          perioStage: stage ?? 'N/A',
          hasCalculusAtBalancedThreshold: hasCalculus,
          numberOfQuadrantsWithPerioRecs,
        });
      }}
      onDrag={() => {
        // Clicking anywhere in the modal (e.g., mode toggle or close button)
        // invokes onDragStart and onDragStop before other click events, but
        // onDrag is fired only when the user actually moves the panel. For this
        // reason, we're firing drag analytics in onDrag (once per drag via
        // ref).
        if (!hasFiredDragEventRef.current) {
          hasFiredDragEventRef.current = true;
          trackEvent(mixpanelEvents.perioChartDrag, {
            examDate: perioChartData.examDate,
            toothCount: perioChartData.teeth.length,
            isPerioPatient: !!visitSummary?.isPerioPatient,
            perioStage: stage ?? 'N/A',
            hasCalculusAtBalancedThreshold: hasCalculus,
            numberOfQuadrantsWithPerioRecs,
          });
        }
      }}
      onDragStop={() => {
        hasFiredDragEventRef.current = false;
      }}
    >
      <PerioChart mode={perioChartTheme} perioChartData={perioChartData} />
    </ResizableDraggable>
  );

  return (
    <>
      <button
        type="button"
        className="vai-flex vai-justify-center vai-items-center"
        onClick={() => {
          trackEvent(mixpanelEvents.perioChartButtonClick, {
            examDate: perioChartData.examDate,
            toothCount: perioChartData.teeth.length,
            isPerioPatient: !!visitSummary?.isPerioPatient,
            perioStage: stage ?? 'N/A',
            hasCalculusAtBalancedThreshold: hasCalculus,
            numberOfQuadrantsWithPerioRecs,
          });
          setIsOpen(true);
        }}
        aria-label="Open Perio Chart"
      >
        <IconPill
          text={t`Perio Chart`}
          icon={
            <FontAwesomeIcon
              className="vai-text-zinc-50"
              icon={faSquareArrowUpRight}
              size="xs"
            />
          }
        />
      </button>
      {isOpen && createPortal(rndContent, document.body)}
    </>
  );
};
